/**
 * The login page's offline sign-in (container, D20): asks the node what it
 * offers (`GET /local/auth`, polled) and, while the node cannot reach the
 * cloud, shows `OfflineSignIn` — an offline PIN or the site's emergency
 * account (`POST /local/login`). It goes away when the link returns; a
 * refusal because the link came back stays on screen as a sentence, so the
 * operator knows why the form disappeared. Router-free: what happens after a
 * sign-in is the login view's (`onSignedIn`).
 */
import { useState } from 'react';
import { OfflineSignIn } from '../components/OfflineSignIn.js';
import { FieldError } from '../components/ui/field.js';
import { useLocalAuth, useServices } from '../app/hooks.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function OfflineSignInPanel({ onSignedIn }: { readonly onSignedIn: () => void }) {
  const { offline } = useServices();
  const localAuth = useLocalAuth();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (offline === null) return null;

  const status = localAuth.data;
  if (status?.offlineLogin !== true) {
    return error === null ? null : <FieldError data-testid="offline-refusal">{error}</FieldError>;
  }

  const submit = (account: string, secret: string): void => {
    setPending(true);
    setError(null);
    offline
      .login(account, secret)
      .then(onSignedIn)
      .catch((err: unknown) => {
        setError(messageOf(err));
        // A refusal because the link came back: the status says so on its next read; ask now.
        void localAuth.refetch();
      })
      .finally(() => setPending(false));
  };

  return <OfflineSignIn lastCloudContact={status.lastCloudContact} pending={pending} error={error} onSubmit={({ account, secret }) => submit(account, secret)} />;
}
